import { useMemo } from 'react';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { useFormikContext } from 'formik';
import { getDispatchGridColDefs } from '@components/DataGrid/Dispatch/columns';
import { expandSubColumns } from '@components/DataGrid/subColumns';
import { BoardQuery } from '@api/Trinium/types';
import { DispatchLeg } from '@api/Trinium/Endpoints/Dispatch/types';
import { BoardQueryFormFields } from './types';
import { previewRows } from './previewRows';

import styles from './Page.module.css';

const getRowId = (row: DispatchLeg) => row.legId;

/** Cells stay inert so nothing in the preview reads as an edit; headers keep pointer events so the grid can scroll */
const previewSx = { '& .MuiDataGrid-cell': { pointerEvents: 'none' } };

/**
 * Read-only Dispatch Board rendered from the form's current values, so the effect of a change is visible
 * before it is saved. Mirrors the board's forced and pinned columns; interaction is deliberately disabled.
 */
export default function QueryPreview(props: { query: BoardQuery }) {
  const { values } = useFormikContext<BoardQueryFormFields>();

  const { gridColumns, columnVisibilityModel, pinnedColumns } = useMemo(() => {
    const pinnedCols = { left: values.pinnedFields, right: props.query.configuration.pinnedCols.right };
    const previewQuery: BoardQuery = {
      ...props.query,
      // The name is not part of what builds the columns, so it is left out explicitly — typing it must not rebuild the grid
      configuration: { ...props.query.configuration, columns: values.columns, pinnedCols }
    };

    const { columns, hiddenFields } = expandSubColumns(getDispatchGridColDefs(previewQuery));
    return {
      gridColumns: columns,
      pinnedColumns: pinnedCols,
      columnVisibilityModel: { __check__: true, ...hiddenFields }
    };
  }, [props.query, values.columns, values.pinnedFields]);

  return (
    <div className={styles.preview}>
      <span className={styles['section-title']}>Preview</span>

      <DataGridPro
        rows={previewRows}
        columns={gridColumns}
        pinnedColumns={pinnedColumns}
        columnVisibilityModel={columnVisibilityModel}
        getRowId={getRowId}
        rowHeight={50}
        columnHeaderHeight={50}
        hideFooter
        disableColumnMenu
        disableColumnSorting
        disableColumnFilter
        disableColumnResize
        disableRowSelectionOnClick
        disableMultipleRowSelection
        className={styles['preview-grid']}
        sx={previewSx}
      />
    </div>
  );
}
